import { Stack }   from '@mui/material'
import { Center }  from 'components/common/Center'
import Fallback    from 'components/common/Fallback'
import SectionView from 'components/view/SectionView'
import { debounce } from 'lodash'
import React from 'react'
import { useEffect } from 'react'
import { Schema } from 'types/view'

interface SectionItem {
  key: string
  weight?: number
}

interface Stencil {
  direction: 'horizontal' | 'vertical'
  weight?: number
  items: (string | SectionItem | Stencil)[]
}

interface LayoutDetailViewProps {
  schema: Schema
  sections: string[]
  stencil: Stencil
  width: number
}

function getWeight(item: string | SectionItem | Stencil) {
  if (typeof item == 'string') return 1
  else return item.weight || 1
}

/** Render a detail view according to the given stencil */
function LayoutDetailView({ schema, sections, stencil }: LayoutDetailViewProps) {
  const translationBaseKey = `views.details.${schema.key}`
  const augmentedSchema    = { ...schema, kind: "detail", translationBaseKey }
  const [availableWidth, setWidth]  = React.useState<number>(document.getElementById('sticky-banner-body')?.offsetWidth ?? 1400)

  const updateWidth = debounce(() => {
    const newWidth = document.getElementById('sticky-banner-body')?.offsetWidth ?? 1400
    setWidth(newWidth)
  }, 50)

  const resizeObserver = new ResizeObserver(updateWidth)

  useEffect(() => {
    const body = document.getElementById('sticky-banner-body')
    if (!body) return

    resizeObserver.observe(body)
    // disconnect in cleanup to prevent memory leaks
    return () => {
      resizeObserver.disconnect();
    };
  })

  return (
    <Fallback name='detail'>
      <SectionStack schema={augmentedSchema} sections={sections} stencil={stencil} width={availableWidth - 40} />
    </Fallback>
  )
}

function SectionStack({ schema, sections, stencil, width }: LayoutDetailViewProps) {
  const isEnabled: (item: (string | Stencil | SectionItem)) => boolean =
    (item: (string | Stencil | SectionItem)) => typeof item === 'string'
      ? sections.includes(item)
      : 'key' in item
        ? sections.includes(item.key)
        : item.items.filter(isEnabled).length > 0

  const isHorizontal = stencil.direction == 'horizontal'
  const direction = isHorizontal ? 'row' : 'column'
  const align = isHorizontal ? 'flex-start' : 'center'

  const enabledItems = stencil.items.filter(isEnabled)
  const marginAdjust = (enabledItems.length - 1) * 24
  const totalWeight = enabledItems.map(getWeight).reduce((a, b) => a + b)
  const itemWidth = isHorizontal ? (width-marginAdjust) / totalWeight : width

  return (
    <Stack className='details-stack'
           sx={{ width: 'min-content', maxWidth: '100%', boxSizing: 'content-box', alignItems: align, justifyContent: 'flex-start' }}
           direction={direction}
           spacing={3}>
      {enabledItems.map((item, index) => {
        if (typeof item === 'string') {
          const section = schema.sections.find(s => s.key === item)
          return <SectionView key={`string-${item}`} schema={schema} section={{width: itemWidth, ...section}} />
        } else if ('key' in item) {
          const section = schema.sections.find(s => s.key === item.key)
          const weightedWidth = itemWidth * (item.weight || 1)
          return <SectionView key={`section-${item.key}`} schema={schema} section={{width: weightedWidth, ...section}} />
        } else {
          const weightedWidth = itemWidth * (item.weight || 1)
          return <SectionStack key={`stencil-${index}`} schema={schema} sections={sections} stencil={item} width={weightedWidth} />
        }
      })}
    </Stack>
  )
}

export default LayoutDetailView
